/**
 * TransportCollectionTab — Onglet HR "Collecte transport".
 *
 * Liste les employés éligibles à la collecte mensuelle (transportAmount > 0),
 * permet de sélectionner ceux à relancer, de choisir leur provider, puis d'envoyer
 * les demandes en masse via le portail (purpose='transport_monthly').
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Send, RefreshCw, Loader, AlertCircle, CheckCircle2, Clock,
  MailCheck, FileText, ChevronLeft, ChevronRight, Bus, Settings,
} from 'lucide-react';
import {
  getTransportCollectionCandidates,
  launchTransportCollectionCampaign,
  setEmployeeDefaultTransportProvider,
  getTransportProviders,
} from '../../api';
import { useToast } from '../../contexts/NotificationContext';
import { useStructure } from '../../contexts/StructureContext';
import LoadingSpinner from '../LoadingSpinner';

const STATUS_BADGES = {
  pending:     { label: 'À demander',     bg: 'bg-gray-100',   text: 'text-gray-700',   icon: Clock },
  sent:        { label: 'Envoyée',        bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Send },
  in_progress: { label: 'En cours',       bg: 'bg-amber-100',  text: 'text-amber-700',  icon: Clock },
  uploaded:    { label: 'À valider',      bg: 'bg-amber-100',  text: 'text-amber-800',  icon: FileText },
  rejected:    { label: 'Refusée',        bg: 'bg-red-100',    text: 'text-red-700',    icon: AlertCircle },
  validated:   { label: 'Validée',        bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle2 },
  received:    { label: 'Saisie manuelle', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: MailCheck },
};

const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function periodToYM(date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

function previousMonth(date = new Date()) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1));
}

function shiftMonth(period, delta) {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return periodToYM(d);
}

function periodLabel(period) {
  const [y, m] = period.split('-').map(Number);
  return `${MONTHS_FR[m - 1]} ${y}`;
}

export default function TransportCollectionTab({ showAllStructures = false, onNavigate }) {
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);
  const { currentStructure, structures } = useStructure();

  // structureIds CSV envoyé au backend : null = Toutes, sinon ID(s)
  const structureFilterCsv = showAllStructures
    ? null
    : (currentStructure?.id || null);

  const [period, setPeriod] = useState(periodToYM(previousMonth()));
  const [candidates, setCandidates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // selections par employeeId : { checked: bool, providerId: string }
  const [selections, setSelections] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        getTransportCollectionCandidates(period, structureFilterCsv),
        getTransportProviders(),
      ]);
      setCandidates(cRes.data?.candidates || []);
      setProviders(pRes.data || []);
      const init = {};
      (cRes.data?.candidates || []).forEach((c) => {
        const eligibleToSend = ['pending'].includes(c.status);
        init[c.employee.id] = {
          checked: eligibleToSend && !!c.suggestedProviderId && !!c.employee.email,
          providerId: c.suggestedProviderId || '',
        };
      });
      setSelections(init);
    } catch (e) {
      toastRef.current.error('Erreur chargement candidats');
    } finally {
      setLoading(false);
    }
  }, [period, structureFilterCsv]); // toast retiré (utilisé via ref pour éviter refresh en boucle)

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const out = { total: candidates.length, byStatus: {} };
    Object.keys(STATUS_BADGES).forEach((k) => out.byStatus[k] = 0);
    candidates.forEach((c) => { out.byStatus[c.status] = (out.byStatus[c.status] || 0) + 1; });
    return out;
  }, [candidates]);

  const selectedCount = useMemo(
    () => Object.values(selections).filter((s) => s.checked).length,
    [selections]
  );

  const handleLaunch = async () => {
    const items = candidates
      .filter((c) => selections[c.employee.id]?.checked)
      .map((c) => ({
        employeeId: c.employee.id,
        transportProviderId: selections[c.employee.id]?.providerId || c.suggestedProviderId || null,
      }));

    if (items.length === 0) {
      toast.error('Aucun employé sélectionné');
      return;
    }

    if (!window.confirm(`Envoyer ${items.length} demande(s) de justificatif de transport pour ${periodLabel(period)} ?`)) return;

    setSubmitting(true);
    try {
      const { data } = await launchTransportCollectionCampaign({ period, items });
      const c = data.created?.length || 0;
      const s = data.skipped?.length || 0;
      const f = data.failed?.length || 0;
      let msg = `${c} demande(s) envoyée(s)`;
      if (s > 0) msg += ` · ${s} ignorée(s)`;
      if (f > 0) msg += ` · ${f} échec(s)`;
      toast.success(msg);
      if (f > 0) {
        console.warn('[Transport] échecs :', data.failed);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lancement campagne');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (employeeId, providerId) => {
    try {
      await setEmployeeDefaultTransportProvider(employeeId, providerId || null);
      toast.success('Fournisseur par défaut mis à jour');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur mise à jour');
    }
  };

  // Coche toutes les lignes envoyables : statut pending + email + provider connu (sélectionné ou suggéré)
  const toggleAll = (val) => {
    const next = { ...selections };
    let skipped = 0;
    candidates.forEach((c) => {
      const sel = next[c.employee.id] || {};
      const hasProvider = sel.providerId || c.suggestedProviderId;
      const eligible = c.status === 'pending' && c.employee.email && hasProvider;
      if (eligible) {
        next[c.employee.id] = { ...sel, checked: val };
      } else if (val && c.status === 'pending') {
        skipped += 1; // signale les exclus à l'utilisateur
      }
    });
    setSelections(next);
    if (val && skipped > 0) {
      toast.info?.(`${skipped} ligne(s) ignorée(s) — email ou provider manquant`) ||
        toast.success(`${skipped} ligne(s) ignorée(s) (email ou provider manquant)`);
    }
  };

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
            <Bus size={20} className="text-teal-700" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              Collecte transport
              {showAllStructures ? (
                <span className="text-[10px] font-normal bg-primary/10 text-primary px-2 py-0.5 rounded">Toutes les structures</span>
              ) : currentStructure?.name ? (
                <span className="text-[10px] font-normal bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{currentStructure.name}</span>
              ) : null}
            </h2>
            <p className="text-xs text-gray-500">
              Envoie aux employés concernés un lien sécurisé pour transmettre leur justificatif mensuel.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPeriod(shiftMonth(period, -1))}
            className="p-1.5 hover:bg-gray-100 rounded"
            title="Mois précédent"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="text-sm font-semibold text-gray-800 min-w-[140px] text-center">
            {periodLabel(period)}
          </div>
          <button
            onClick={() => setPeriod(shiftMonth(period, 1))}
            className="p-1.5 hover:bg-gray-100 rounded"
            title="Mois suivant"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={load}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-500"
            title="Rafraîchir"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Avertissement si aucun provider configuré */}
      {!loading && providers.length === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            Aucun fournisseur transport n'est configuré. Ouvre <strong>Admin &gt; Fournisseurs transport</strong> pour en créer (TCL, Navigo, SNCF...) avant de lancer une campagne.
          </div>
        </div>
      )}

      {/* Stats */}
      {!loading && candidates.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.entries(STATUS_BADGES).map(([key, b]) => {
            const Icon = b.icon;
            return (
              <div key={key} className={`p-3 rounded-lg ${b.bg} flex items-center gap-2`}>
                <Icon size={16} className={b.text} />
                <div>
                  <div className={`text-xs ${b.text}`}>{b.label}</div>
                  <div className={`text-lg font-semibold ${b.text}`}>{stats.byStatus[key] || 0}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barre actions */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-700">{selectedCount} sélectionné(s)</span>
          <button onClick={() => toggleAll(true)} className="text-xs text-primary hover:underline">Tout cocher (à demander)</button>
          <button onClick={() => toggleAll(false)} className="text-xs text-gray-500 hover:underline">Décocher tout</button>
        </div>
        <button
          onClick={handleLaunch}
          disabled={submitting || selectedCount === 0}
          className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-60 flex items-center gap-2"
        >
          {submitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
          {submitting ? 'Envoi...' : `Envoyer pour ${periodLabel(period)}`}
        </button>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="default" text="Chargement..." inline />
        </div>
      ) : candidates.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-8 text-center text-sm text-gray-500">
          <AlertCircle className="mx-auto mb-3 text-gray-400" size={28} />
          <p>Aucun employé éligible pour ce mois.</p>
          <p className="text-xs mt-1">Un employé doit avoir un montant transport &gt; 0 sur sa fiche et être actif pour apparaître ici.</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="text-left px-3 py-2 w-10"></th>
                <th className="text-left px-3 py-2">Employé</th>
                <th className="text-left px-3 py-2">Structure</th>
                <th className="text-right px-3 py-2">Montant</th>
                <th className="text-left px-3 py-2">Provider</th>
                <th className="text-left px-3 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => {
                const e = c.employee;
                const sel = selections[e.id] || {};
                const sb = STATUS_BADGES[c.status] || STATUS_BADGES.pending;
                const Icon = sb.icon;
                const canSend = c.status === 'pending';
                const noEmail = !e.email;
                const noProvider = !sel.providerId && !c.suggestedProviderId;

                return (
                  <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        disabled={!canSend || noEmail || noProvider}
                        checked={!!sel.checked}
                        onChange={(ev) => setSelections((s) => ({ ...s, [e.id]: { ...s[e.id], checked: ev.target.checked } }))}
                        title={noEmail ? 'Email manquant' : noProvider ? 'Choisis un provider' : !canSend ? 'Déjà demandé ou reçu' : ''}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => onNavigate?.('hr', { employeeId: e.id })}
                        className="font-medium text-gray-800 hover:text-primary hover:underline cursor-pointer text-left"
                        title="Ouvrir la fiche de l'employé"
                      >
                        {e.firstName} {e.lastName}
                      </button>
                      {noEmail && (
                        <div className="text-[10px] text-red-600 flex items-center gap-1">
                          <AlertCircle size={10} /> Email manquant sur la fiche
                        </div>
                      )}
                      {!noEmail && (
                        <div className="text-[10px] text-gray-400 truncate max-w-[200px]">{e.email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{e.structure?.name || '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-700">{e.transportAmount} €</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <select
                          value={sel.providerId || c.suggestedProviderId || ''}
                          onChange={(ev) => {
                            const v = ev.target.value;
                            setSelections((s) => ({ ...s, [e.id]: { ...s[e.id], providerId: v } }));
                          }}
                          className={`text-xs border rounded px-2 py-1 min-w-[120px] max-w-[160px] ${noProvider && canSend ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                        >
                          <option value="">— Choisir un provider —</option>
                          {providers.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        {(sel.providerId || c.suggestedProviderId) && !e.defaultTransportProvider && (
                          <button
                            onClick={() => handleSetDefault(e.id, sel.providerId || c.suggestedProviderId)}
                            className="px-2 py-1 text-[10px] text-primary hover:bg-primary/5 border border-primary/30 rounded flex items-center gap-1 whitespace-nowrap"
                            title="Enregistrer ce provider comme défaut sur la fiche de l'employé"
                          >
                            <Settings size={10} /> Définir par défaut
                          </button>
                        )}
                      </div>
                      {e.defaultTransportProvider && (
                        <div className="text-[10px] text-emerald-700 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 size={10} /> Défaut sur la fiche : {e.defaultTransportProvider.name}
                        </div>
                      )}
                      {!e.defaultTransportProvider && c.suggestedProviderId && (
                        <div className="text-[10px] text-gray-400 mt-0.5 italic">
                          Suggéré depuis le dernier mois
                        </div>
                      )}
                      {noProvider && canSend && (
                        <div className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                          <AlertCircle size={10} /> Choisis un provider pour activer la coche
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${sb.bg} ${sb.text}`}>
                        <Icon size={10} /> {sb.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        <strong>Comment ça marche :</strong> coche les employés à relancer, vérifie leur provider, puis envoie. Chaque employé reçoit un email avec un lien sécurisé. Une fois le PDF uploadé via le portail, il est automatiquement tagué transport + mois + provider, et la feuille de temps correspondante est cochée. Si tu ne refuses pas le document dans les 5 jours, il est auto-validé.
      </div>
    </div>
  );
}
